import groq from 'groq';

import { getLanguageSuffixByMarket } from '../../data/sanity/queries';
import {
  contentDefinedByLocale,
  contentFilterByLocale,
  getImageQueryProperties,
  getIrrelevantAndRelevantCountryFilter,
} from '../../data/sanity/queryFragments';
import type { SemboMarket } from '../../utils/markets';

export const creatorBySlug = (market: SemboMarket, usernameSlug: string) => {
  const language = getLanguageSuffixByMarket(market);

  return groq`*[_type == "creator" && username.current == "${usernameSlug}" && ${contentDefinedByLocale(market)}] {
    _id,
    firstName,
    lastName,
    secondLastName,
    unlisted,
    ...(${contentFilterByLocale(market)} {
      bio[]{
        ...,
        markDefs[]{
          ...,
          _type == "link" => {
            "content": ${contentFilterByLocale(market, { isReference: '@.reference->' })} {
              _type,
              slug
            },
            "username": @.reference->username
          }
        }
      },
      headline,
      searchLabel,
      searchPlaceholder,
    }),
    brandName,
    ecoFriendly,
    role,
    "facts": facts[${contentDefinedByLocale(market, { isReference: true })}] -> {
      _id,
      ...(image-> {
        "image": {
          ${getImageQueryProperties(market)}
        }
      }),
      ...(${contentFilterByLocale(market)} {
        headline,
        description[]{
          ...,
          markDefs[]{
            ...,
            _type == "link" => {
              "content": ${contentFilterByLocale(market, { isReference: '@.reference->' })} {
                _type,
                slug
              },
              "username": @.reference->username
            }
          }
        }
      }),
    },
    "tags": tags[${contentDefinedByLocale(market, { isReference: true })}] -> {
      _id,
      ...(${contentFilterByLocale(market)} {
        name,
        "slug": slug.current
      }),
    },
    "labels": labels[] -> {
      _id,
      "name": name${language}
    },
    ...(profileImage-> {
      "profileImage": {
        ${getImageQueryProperties(market)}
      }
    }),
    ...(logo-> {
      "logo": {
        ${getImageQueryProperties(market)}
      }
    }),
    'tripsCollections': coalesce(tripsCollections[] -> {
      _id,
      'name': name${language},
      'tripIds': coalesce(trips[
        @->._type == "spotlight" ||
        (
          @->._type == "trip" &&
          ${contentDefinedByLocale(market, { isReference: true })}
        )
      ]._ref, []),
      'preamble': preamble${language},
    }, []),
    'hotelCollections' : coalesce(hotelCollections[@->name${language} != null] -> {
      _id,
      'name': name${language},
      'preamble': preamble${language},
      'hotelQueries': hotelQueries,
    }, []),
    'teasers': coalesce(teasers[
      (@->_type == "teaser" && ${contentDefinedByLocale(market, { isReference: true })}) ||
      (@->_type == "styledTeaser" && ${contentDefinedByLocale(market, { isReference: '@->teaser->' })})
    ]{ _ref }, []),
    theme,
    semboAffiliateId,
}[0]`;
};

export const proudPartnersCreatorsLogos = (market: SemboMarket) => {
  return groq`*[_type == "creator" && proudPartner == true && ${getIrrelevantAndRelevantCountryFilter(market)} ] {
  "altText": coalesce(brandName, companyName),
  ...(logo-> {
      "logo": {
        ${getImageQueryProperties(market)}
      }
    }),
}`;
};

export const highlightedCampaignsCollectionByCreatorId = (
  market: SemboMarket,
  creatorId: string
) => {
  const language = getLanguageSuffixByMarket(market);

  return groq`
    *[_type == "creator" && _id == "${creatorId}" && highlightedCampaigns -> name${language} != null][0].highlightedCampaigns -> {
      "name": name${language},
      "preamble": preamble${language},
      "campaigns": campaigns[${contentDefinedByLocale(market, { isReference: true })}]
    }
  `;
};
